import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import { useAdminAccount } from '@/context/AdminAccountContext'
import { useToast } from '@/context/ToastContext'
import { AccountService, AdminLoginError } from '@common/services/account-service'

export const useLogin = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { setAdminAccount } = useAdminAccount()
  const { showErrorMessage, showSuccessMessage } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setEmailError(null)
    setPasswordError(null)

    // バリデーション: 必須チェック
    const trimmedEmail = email.trim()
    const trimmedPassword = password.trim()
    let hasError = false

    if (!trimmedEmail) {
      setEmailError('メールアドレスは必須です')
      hasError = true
    }

    if (!trimmedPassword) {
      setPasswordError('パスワードは必須です')
      hasError = true
    }

    if (hasError) {
      // バリデーションエラー時もトースト通知を表示
      if (!trimmedEmail && !trimmedPassword) {
        showErrorMessage('メールアドレスとパスワードを入力してください')
      } else if (!trimmedEmail) {
        showErrorMessage('メールアドレスを入力してください')
      } else if (!trimmedPassword) {
        showErrorMessage('パスワードを入力してください')
      }
      return
    }

    setIsLoading(true)

    try {
      const { account, isFirstLogin } = await new AccountService().loginAsAdmin(trimmedEmail, trimmedPassword)

      if (isFirstLogin) {
        navigate('/first-login')
      } else {
        setAdminAccount(account)
        navigate('/dashboards')
      }
    } catch (err) {
      if (err instanceof AdminLoginError) {
        showErrorMessage(err.message ?? 'ログインに失敗しました')
        return
      }
      console.error('Login error:', err)
      // エラーメッセージを日本語で表示
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/invalid-email') {
        const errorMsg = 'メールアドレスの形式が正しくありません'
        setEmailError(errorMsg)
        showErrorMessage(errorMsg)
      } else if (err.code === 'auth/user-disabled') {
        const errorMsg = 'このアカウントは無効化されています'
        setEmailError(errorMsg)
        showErrorMessage(errorMsg)
      } else if (err.code === 'auth/user-not-found') {
        const errorMsg = 'メールアドレスまたはパスワードが正しくありません'
        setEmailError(errorMsg)
        setPasswordError(errorMsg)
        showErrorMessage(errorMsg)
      } else if (err.code === 'auth/wrong-password') {
        const errorMsg = 'メールアドレスまたはパスワードが正しくありません'
        setEmailError(errorMsg)
        setPasswordError(errorMsg)
        showErrorMessage(errorMsg)
      } else if (err.code === 'auth/invalid-credential') {
        const errorMsg = 'メールアドレスまたはパスワードが正しくありません'
        setEmailError(errorMsg)
        setPasswordError(errorMsg)
        showErrorMessage(errorMsg)
        } else {
          const errorMsg = 'ログインに失敗しました。もう一度お試しください'
          showErrorMessage(errorMsg)
        }
      } else {
        // FirebaseError以外のエラーの場合
        const errorMsg = 'ログインに失敗しました。もう一度お試しください'
        showErrorMessage(errorMsg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    isLoading,
    emailError,
    passwordError,
    setEmailError,
    setPasswordError,
    handleSubmit,
    togglePasswordVisibility,
  }
}

